import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Check, ChevronDown, ChevronUp, Copy, FolderTree, Loader2, MapPin, Pencil, RefreshCw, Server } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { parseHandoverRefusal, type HandoverRefusalCode } from '@/lib/trpc-error-utils';
import { FolderPicker } from '@/features/storage/components/FolderPicker';
import { useClientSharedSkuMappingCount } from '@/features/inventory/hooks/useSharedSkus';
import { TencentBucketSelect, TENCENT_BUCKET_NAME_RE } from './TencentBucketSelect';
import { type ShippingProvider } from '@/features/production-settings/constants/shipping-providers';
import { ShopifyConnectionPanel } from './ShopifyConnectionPanel';
import { ClientShippingControls, type LocationDefaultProvider } from './ClientShippingControls';
import { JunctionGrantRow } from './JunctionGrantRow';
import { ClientBlankSupplyGrant } from './ClientBlankSupplyGrant';

interface SharedLocationsCardProps {
  locationId: string;
  locationName: string;
}

/**
 * Print-folder (customs / prints) path validator. Mirrors the backend's
 * `validateFolderPath` (apps/backend/src/lib/storage/folder-validators.ts) but
 * treats '' as valid: `updateClientPrintFolders` accepts '' as a first-class
 * value meaning the bucket/prefix ROOT. Returns true only when a NON-EMPTY
 * value is malformed.
 */
const PRINT_FOLDER_ALLOWED_CHARS = /^[A-Za-z0-9/_-]+$/;
const isInvalidPrintFolder = (value: string): boolean => {
  if (value === '') return false; // '' === bucket root, a first-class choice
  if (value.length > 256) return true;
  if (value.startsWith('/') || value.endsWith('/')) return true;
  if (value.includes('..') || value.includes('\\') || value.includes('://')) return true;
  if (value === '__CLEAR__') return true; // reserved sentinel (backend clears on this)
  return !PRINT_FOLDER_ALLOWED_CHARS.test(value);
};

/**
 * Editor submit-guard for one print-folder field. Tri-state: `null` = inherit
 * the owner default, '' = bucket root, a non-empty string = a named folder.
 * null and '' are always allowed; only a malformed named folder is invalid.
 */
const isPrintFolderOverrideInvalid = (value: string | null): boolean =>
  value !== null && isInvalidPrintFolder(value);

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

interface ConnectedRow {
  location_id?: string;
  client_organization_id: string;
  client_shopify_location_id: number | null;
  status?: 'pending_approval' | 'active' | 'revoked';
  claimed_at?: string;
  approved_at?: string | null;
  revoked_reason?: string | null;
  client_org?: { name?: string } | null;
  // BLU-3PL client alias: owner-set nickname for this client at this
  // location. Shown in place of client_org.name (here + the Order Queue)
  // when set.
  client_alias?: string | null;
  // BLU-368 Plan 47.02-05: junction prefix + parent location's
  // production_settings (decides whether to show Edit folder).
  print_file_prefix?: string | null;
  // Phase 47.02.3-LITE: per-junction Tencent COS bucket assignment. NULL
  // means "fall back to org Vault default bucket" — preserved for
  // owner-direct + pre-LITE rows.
  tencent_bucket_name?: string | null;
  // BLU-641: per-junction print-folder overrides inside the bucket/prefix.
  // NULL = inherit the owner-level default folder; '' = bucket/prefix root;
  // a string = an explicit sub-folder. `customs_folder` scopes customs
  // artwork, `prints_folder` scopes standard print files.
  customs_folder?: string | null;
  prints_folder?: string | null;
  // BLU C2: per-junction fulfillment write-back opt-in. When true (and the
  // effective shipping provider for this client at this location is
  // Manual), completing a shipment manually marks the CLIENT's Shopify
  // order fulfilled via their own vault creds. Defaults false — see
  // `.planning/findings/2026-07-10-c2-fulfillment-writeback-handover.md`.
  fulfillment_writeback_enabled?: boolean;
  // BLU-711 A1: owner-set opt-in letting this client adjust the stock rows it
  // owns at this location. Default false preserves owner-manages-location.
  client_stock_writes_enabled?: boolean;
  // Phase 92 (BLU-1269, D-08): owner-set consent letting this client build
  // products on the owner's shared blanks at this location. Default false;
  // off means those products read as out of stock (D-22, fail closed).
  client_blank_supply_enabled?: boolean;
  // BLU-711 A2: whether OUR stocktake against this client's rows writes
  // straight through ('auto_apply', the default, i.e. today's behaviour) or
  // stages as a proposal the client approves ('review'). Independent of
  // client_stock_writes_enabled — that one is about their writes, this one is
  // about ours.
  stocktake_review_mode?: 'auto_apply' | 'review';
  // BLU per-client shipping: per-junction shipping-provider override. NULL
  // (or an unrecognised/legacy value) means "inherit the location's default
  // provider" — see `resolveShippingProviderForOrder`. Local to this card;
  // NOT added to `features/locations/types.ts`.
  shipping_provider?: ShippingProvider | null;
  // BLU-1460: the Shopify tag added when a Hand over order is fully handed
  // over. Set only while shipping_provider is 'handover'.
  handover_shopify_tag?: string | null;
  locations?: {
    id?: string;
    name?: string;
    owner_organization_id?: string;
    // Phase 47.02.2 Plan 04 generalized the folder-picker visibility to
    // any backend that uses per-client folder prefixes — Tencent COS (BLU-368)
    // and GCS (Plan 02 factory derives `org_<owner>/clients/<client>/`).
    // Azure container routing has no folder concept and stays hidden.
    production_settings?: {
      print_file_source?: 'azure' | 'tencent_cos' | 'gcs';
      // Phase 47.02.3-LITE: per-location Tencent region override. Used to
      // suppress a redundant region write when the bucket we're assigning
      // already lives in the region we already have set. ap-singapore added
      // 2026-05-24 for Bel Espirit's intl 3PL account.
      tencent_region?: 'ap-guangzhou' | 'ap-shanghai' | 'ap-shenzhen-fsi' | 'ap-beijing' | 'ap-singapore';
    } | null;
  } | null;
}

interface PendingRow {
  location_id?: string;
  client_organization_id: string;
  client_shopify_location_id: number | null;
  claimed_at?: string;
  client_org?: { name?: string } | null;
}

interface RevokeTarget {
  clientOrganizationId: string;
  clientName: string;
  reason: string;
}

/**
 * Owner-side admin UI for the BLU-367 3PL shared-location flow. Mounts on the
 * location detail row when the location's owner_organization_id matches the
 * active org. Each card scopes its claim-code panel + connected-clients list
 * to its own locationId (the parent component renders one card per location).
 */
export default function SharedLocationsCard({ locationId, locationName }: SharedLocationsCardProps) {
  const { t: tSettings } = useTranslation('settings');
  const [latestClaimCode, setLatestClaimCode] = useState<string | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<RevokeTarget | null>(null);
  const [glowKey, setGlowKey] = useState(0);
  const codePanelRef = useRef<HTMLDivElement>(null);
  // BLU-368 Plan 47.02-05: which pending-claim row's FolderPicker is open.
  const [openApprovePickerForClientId, setOpenApprovePickerForClientId] = useState<string | null>(null);
  const [openEditPickerForClientId, setOpenEditPickerForClientId] = useState<string | null>(null);
  // Phase 47.02.3-LITE: per-junction Tencent bucket picker visibility,
  // independent of the folder picker so an operator can edit either without
  // closing the other.
  const [openBucketPickerForClientId, setOpenBucketPickerForClientId] = useState<string | null>(null);
  // Pending bucket choice (per client). Saved on explicit confirm — no
  // keystroke-driven mutation.
  const [pendingBucketByClient, setPendingBucketByClient] = useState<Record<string, string>>({});
  // BLU-641 approve-panel bucket override. Only one approve panel is open at a
  // time (openApprovePickerForClientId is a single value), so a single string
  // suffices. Re-drives the FolderPicker via `bucketOverride` and rides along
  // with the approve mutation as `tencentBucketName`. '' = today's default
  // (browse the location's default bucket, don't send tencentBucketName).
  const [approveBucket, setApproveBucket] = useState('');
  // BLU-641 per-junction print-folder (customs / prints) editor. Pending value
  // is `string | null`: null = inherit the owner default, '' = bucket root, a
  // string = an explicit folder. Absent key = editor never opened for that row.
  const [openPrintFoldersEditorForClientId, setOpenPrintFoldersEditorForClientId] = useState<string | null>(null);
  const [pendingCustomsFolderByClient, setPendingCustomsFolderByClient] = useState<Record<string, string | null>>({});
  const [pendingPrintsFolderByClient, setPendingPrintsFolderByClient] = useState<Record<string, string | null>>({});
  // BLU-3PL client alias: which client's nickname editor is open + its
  // in-progress (unsaved) value, mirroring the bucket free-text pattern.
  const [openAliasEditorForClientId, setOpenAliasEditorForClientId] = useState<string | null>(null);
  const [pendingAliasByClient, setPendingAliasByClient] = useState<Record<string, string>>({});
  // BLU C2: enabling fulfillment write-back writes to the client's live
  // Shopify store on the next manual ship, so it gets an explicit confirm
  // (mirrors `pendingRevoke` below). Disabling never confirms.
  const [pendingWritebackConfirm, setPendingWritebackConfirm] = useState<{
    clientOrganizationId: string;
    clientName: string;
  } | null>(null);

  // Esc closes any open picker.
  useEffect(() => {
    if (
      !openApprovePickerForClientId
      && !openEditPickerForClientId
      && !openBucketPickerForClientId
      && !openAliasEditorForClientId
      && !openPrintFoldersEditorForClientId
    ) {
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenApprovePickerForClientId(null);
        setOpenEditPickerForClientId(null);
        setOpenBucketPickerForClientId(null);
        setOpenAliasEditorForClientId(null);
        setOpenPrintFoldersEditorForClientId(null);
        setApproveBucket('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    openApprovePickerForClientId,
    openEditPickerForClientId,
    openBucketPickerForClientId,
    openAliasEditorForClientId,
    openPrintFoldersEditorForClientId,
  ]);

  // Whether a code is currently set in the DB (so we can distinguish "no code
  // yet" from "code is set but we don't have the plaintext anymore").
  const codeStatusQuery = trpc.locations.getClaimCodeStatus.useQuery(
    { locationId },
    { staleTime: 30_000 },
  );

  const utils = trpc.useUtils();
  const connectedQuery = trpc.locations.listConnectedClients.useQuery(undefined, {
    staleTime: 30_000,
  });
  const pendingQuery = trpc.locations.listPendingClaims.useQuery(undefined, {
    staleTime: 15_000,
  });

  // BLU-641: the pending rows don't carry the location's print_file_source, so
  // resolve it from the org's location settings (the same read the production
  // settings page uses). Prefetched whenever this card has a pending claim so
  // the approve-panel bucket selector is present the instant the panel opens
  // (no layout shift). Cheap + shared: staleTime keeps it off the hot path.
  // Also needed for an active junction: a client with no provider override
  // inherits this location's default (org fallback included), which the
  // provider picker names and which decides whether the write-back switch
  // applies to them.
  const locationSettingsQuery = trpc.productionSettings.getLocationSettings.useQuery(undefined, {
    staleTime: 60_000,
    enabled:
      ((pendingQuery.data ?? []) as PendingRow[]).some((r) => r.location_id === locationId)
      || ((connectedQuery.data ?? []) as ConnectedRow[]).some(
        (r) => r.location_id === locationId && r.status === 'active',
      ),
  });
  const locationSettingsRow = locationSettingsQuery.data?.find((l) => l.id === locationId);
  const approveIsTencent = locationSettingsRow?.printFileSource === 'tencent_cos';
  // Never guessed: until the location's own default is read, an inheriting
  // client's write-back switch cannot be turned on. Loaded-but-absent is
  // treated as a failed read so it gets a visible Retry, not a silent lock.
  const locationDefault: LocationDefaultProvider = locationSettingsRow
    ? { status: 'ready', provider: locationSettingsRow.shippingProvider }
    : locationSettingsQuery.isError || locationSettingsQuery.data
      ? { status: 'error', retry: () => void locationSettingsQuery.refetch() }
      : { status: 'loading' };

  const refreshAll = () => {
    void codeStatusQuery.refetch();
    void connectedQuery.refetch();
    void pendingQuery.refetch();
  };

  const rotateMutation = trpc.locations.rotateClaimCode.useMutation({
    onSuccess: (result) => {
      setLatestClaimCode(result.claimCode);
      setRotateOpen(false);
      // One-shot unlock-glow on the code panel border to re-anchor the moment
      // in the project's motion vocabulary.
      setGlowKey((k) => k + 1);
      toast({
        title: 'New claim code ready',
        description:
          result.pendingClaimsRevoked > 0
            ? `${result.pendingClaimsRevoked} pending claim${
                result.pendingClaimsRevoked === 1 ? '' : 's'
              } cancelled. Share the new code with your client.`
            : 'New claim code copied. Share it with your client.',
      });
      navigator.clipboard.writeText(result.claimCode).catch(() => undefined);
      setCodeCopied(true);
      window.setTimeout(() => setCodeCopied(false), 1400);
      refreshAll();
    },
    onError: (err) => {
      toast({ title: 'Could not rotate code', description: err.message, variant: 'destructive' });
    },
  });

  const approveMutation = trpc.locations.approveClientClaim.useMutation({
    onSuccess: (result) => {
      // BLU-368 Plan 47.02-05 / BLU-641: handle the recoverable cases where
      // junction approval landed but the prefix and/or bucket write failed
      // (rate-limit / transient). Junction is `active` either way; operator
      // can fix via Edit folder / the bucket control on the connection row.
      // Staying silent on bucketSetFailed would mean the client's prints
      // quietly route to the org DEFAULT bucket — the operator must know.
      const flags = result as { prefixSetFailed?: boolean; bucketSetFailed?: boolean } | undefined;
      const prefixSetFailed = flags?.prefixSetFailed === true;
      const bucketSetFailed = flags?.bucketSetFailed === true;
      if (prefixSetFailed || bucketSetFailed) {
        const failed = [
          ...(bucketSetFailed ? ['bucket'] : []),
          ...(prefixSetFailed ? ['folder'] : []),
        ].join(' and ');
        toast({
          title: `Approval saved, but the ${failed} couldn’t be set`,
          description: 'Use the bucket and folder controls on the connection to retry.',
        });
      } else {
        toast({ title: 'Access approved', description: 'Their orders will start syncing here.' });
      }
      setOpenApprovePickerForClientId(null);
      setApproveBucket('');
      refreshAll();
    },
    onError: (err) => {
      // A re-claimed client still on Hand over needs a hand over bench here.
      const description =
        parseHandoverRefusal(err) === 'HANDOVER_BENCH_MISSING'
          ? tSettings('sharedLocations.shipping.handoverBenchMissing')
          : err.message;
      toast({ title: 'Could not approve', description, variant: 'destructive' });
    },
  });

  // BLU-368 Plan 47.02-05: post-approval folder edit. Shares 30/min/user
  // mutationBuckets with approve/revoke/rotate per round-3 review — only
  // fires on explicit confirm, never on keystrokes.
  const updatePrefixMutation = trpc.locations.updateClientPrefix.useMutation({
    onSuccess: () => {
      toast({ title: 'Folder updated', description: 'Future prints will use the new folder.' });
      setOpenEditPickerForClientId(null);
      refreshAll();
    },
    onError: (err) => {
      toast({ title: 'Could not update folder', description: err.message, variant: 'destructive' });
    },
  });

  // BLU-3PL client alias: owner-set nickname per junction. Cosmetic field —
  // no audit trail, unlike the folder/bucket mutations above.
  const updateAliasMutation = trpc.locations.updateClientAlias.useMutation({
    onSuccess: () => {
      toast({ title: 'Nickname updated' });
      setOpenAliasEditorForClientId(null);
      refreshAll();
    },
    onError: (err) => {
      toast({ title: 'Could not update nickname', description: err.message, variant: 'destructive' });
    },
  });

  // BLU C2: per-junction fulfillment write-back toggle. Disable fires
  // immediately (no confirm); enable is gated behind `pendingWritebackConfirm`
  // below since it starts writing to the client's live Shopify store on their
  // next manual ship.
  const updateWritebackMutation = trpc.locations.updateFulfillmentWriteback.useMutation({
    onSuccess: (_data, vars) => {
      // Reflect the confirmed value in the cached list immediately so the
      // switch moves with the toast instead of waiting on refreshAll()'s
      // background refetch (which then reconciles with server truth).
      utils.locations.listConnectedClients.setData(undefined, (prev) =>
        prev?.map((row) => {
          const junction = row as ConnectedRow;
          return junction.location_id === vars.locationId &&
            junction.client_organization_id === vars.clientOrganizationId
            ? ({ ...junction, fulfillment_writeback_enabled: vars.enabled } as typeof row)
            : row;
        }),
      );
      toast({
        title: vars.enabled ? 'Fulfilment write-back enabled' : 'Fulfilment write-back disabled',
      });
      setPendingWritebackConfirm(null);
      refreshAll();
    },
    onError: (err) => {
      toast({
        title: 'Could not update fulfilment write-back',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  // BLU-711 A1: per-junction "client manages own stock" toggle. Lets this
  // client adjust the stock rows IT OWNS at this location; default off keeps
  // BLU-681 owner-manages-location authority. Grants no production-floor or
  // print-routing authority, and the client still cannot touch anyone else's
  // rows — so no enable-confirm gate, unlike write-back (which writes to the
  // client's live Shopify store).
  // Toast copy needs the client's name, but alias/orgName are only derived
  // inside the row map far below. Read them off the same cache the rows render
  // from so a multi-client card names WHICH junction changed.
  const clientLabelFor = (clientOrganizationId: string): string => {
    const row = (utils.locations.listConnectedClients.getData() ?? []).find((r) => {
      const j = r as ConnectedRow;
      return j.location_id === locationId && j.client_organization_id === clientOrganizationId;
    }) as ConnectedRow | undefined;
    return row?.client_alias ?? row?.client_org?.name ?? 'This client';
  };

  // Params deliberately not named `locationId` — that would shadow the
  // component prop and invite passing the wrong one.
  const patchStockWritesInCache = (
    targetLocationId: string,
    targetClientOrgId: string,
    enabled: boolean,
  ) =>
    utils.locations.listConnectedClients.setData(undefined, (prev) =>
      prev?.map((row) => {
        const junction = row as ConnectedRow;
        return junction.location_id === targetLocationId &&
          junction.client_organization_id === targetClientOrgId
          ? ({ ...junction, client_stock_writes_enabled: enabled } as typeof row)
          : row;
      }),
    );

  const updateClientStockMutation = trpc.locations.updateClientStockWrites.useMutation({
    // Optimistic: a bare Switch with a server round-trip otherwise sits still,
    // which reads as "nothing happened". Write-back gets away with the
    // success-only patch because its confirm dialog hides the latency.
    onMutate: async (vars) => {
      // Cancel in-flight refetches so a background response can't overwrite
      // the optimistic patch between now and settlement.
      await utils.locations.listConnectedClients.cancel();
      // Snapshot only THIS row's prior value — a whole-list snapshot would
      // clobber a concurrent toggle on another row when this one fails.
      const row = (utils.locations.listConnectedClients.getData() ?? []).find((r) => {
        const j = r as ConnectedRow;
        return (
          j.location_id === vars.locationId &&
          j.client_organization_id === vars.clientOrganizationId
        );
      }) as ConnectedRow | undefined;
      const previousEnabled = row?.client_stock_writes_enabled === true;
      patchStockWritesInCache(vars.locationId, vars.clientOrganizationId, vars.enabled);
      return { previousEnabled };
    },
    onError: (err, vars, ctx) => {
      // Roll back just the failed row.
      patchStockWritesInCache(
        vars.locationId,
        vars.clientOrganizationId,
        ctx?.previousEnabled ?? false,
      );
      toast({
        title: 'Could not update stock permissions',
        description: err.message,
        variant: 'destructive',
      });
    },
    onSuccess: (_data, vars) => {
      const label = clientLabelFor(vars.clientOrganizationId);
      toast({
        title: vars.enabled ? 'Stock adjustments enabled' : 'Stock adjustments disabled',
        description: vars.enabled
          ? `${label} can now adjust their own stock at ${locationName}.`
          : `${label} can no longer adjust their own stock at ${locationName}.`,
      });
      refreshAll();
    },
  });

  // BLU-711 A2: does OUR count against this client's rows apply immediately,
  // or does it go to them for approval? Reversible either way, so it follows
  // the shipping-provider pattern (pick, patch, toast) rather than write-back's
  // confirm gate. Copy stays calm: neither mode is a warning state.
  const updateStocktakeReviewModeMutation = trpc.locations.updateStocktakeReviewMode.useMutation({
    onSuccess: (_data, vars) => {
      utils.locations.listConnectedClients.setData(undefined, (prev) =>
        prev?.map((row) => {
          const junction = row as ConnectedRow;
          return junction.location_id === vars.locationId &&
            junction.client_organization_id === vars.clientOrganizationId
            ? ({ ...junction, stocktake_review_mode: vars.mode } as typeof row)
            : row;
        }),
      );
      const label = clientLabelFor(vars.clientOrganizationId);
      toast({
        title: vars.mode === 'review' ? 'Stocktake review on' : 'Stocktake review off',
        description:
          vars.mode === 'review'
            ? `Counts you run on ${label}'s stock at ${locationName} now go to them for approval.`
            : `Counts you run on ${label}'s stock at ${locationName} now apply immediately.`,
      });
      refreshAll();
    },
    onError: (err) => {
      toast({
        title: 'Could not update stocktake review',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  // BLU per-client shipping: which provider ships this client's orders from
  // this location. Mostly a routing choice, but choosing YunExpress is also a
  // live-store write: Print Run marks the client's Shopify orders fulfilled
  // when each label prints. The backend gates that choice to admins and tells
  // the client's admins; there is no confirm dialog here yet (unlike
  // write-back). Cache-patches immediately on success so the Select reflects
  // the confirmed value without waiting on refreshAll()'s background refetch.
  //
  // Hand over: the save carries the tag, and its refusals (no hand over bench,
  // store connection cannot add tags) show as a line under that client's
  // provider box rather than a toast with the raw marker message.
  const [shippingRefusal, setShippingRefusal] = useState<{
    locationId: string;
    clientOrganizationId: string;
    code: HandoverRefusalCode;
  } | null>(null);
  const updateShippingProviderMutation = trpc.locations.updateClientShippingProvider.useMutation({
    onMutate: () => setShippingRefusal(null),
    onSuccess: (_data, vars) => {
      utils.locations.listConnectedClients.setData(undefined, (prev) =>
        prev?.map((row) => {
          const junction = row as ConnectedRow;
          return junction.location_id === vars.locationId &&
            junction.client_organization_id === vars.clientOrganizationId
            ? ({
                ...junction,
                shipping_provider: vars.shippingProvider,
                handover_shopify_tag: vars.shippingProvider === 'handover' ? vars.handoverTag ?? null : null,
              } as typeof row)
            : row;
        }),
      );
      toast({ title: 'Shipping provider updated' });
      refreshAll();
    },
    onError: (err, vars) => {
      const refusal = parseHandoverRefusal(err);
      if (refusal === 'HANDOVER_BENCH_MISSING' || refusal === 'HANDOVER_TAG_SCOPE_MISSING') {
        setShippingRefusal({
          locationId: vars.locationId,
          clientOrganizationId: vars.clientOrganizationId,
          code: refusal,
        });
        return;
      }
      toast({
        title: 'Could not update shipping provider',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  // Phase 47.02.3-LITE: per-junction Tencent bucket assignment.
  // Loads lazily — only fetched when any bucket picker on the card opens.
  // Graceful 403 fallback handled inline by `data.supported` flag.
  const tencentBucketsQuery = trpc.printAgent.listTencentBuckets.useQuery(undefined, {
    enabled:
      openBucketPickerForClientId !== null
      || (openApprovePickerForClientId !== null && approveIsTencent),
    staleTime: 60_000,
  });

  // Phase 47.02.3-LITE — the bucket assignment fires a region auto-set as a
  // best-effort follow-up. The factory routes prints to
  // `https://cos.<region>.myqcloud.com/<bucket>`, so if the bucket lives in a
  // region different from the location's currently configured `tencent_region`
  // (or different from the org Vault region when no per-location override is
  // set), the first print after onboarding 403s with a `PermanentRedirect`.
  // The frontend already has the bucket entry's region in hand from
  // `listTencentBuckets`, so we shoehorn the region write into the same Save
  // click without exposing a two-step UX.
  const setLocationTencentRegionFromBucketMutation =
    trpc.productionSettings.setLocationTencentRegion.useMutation({
      // Silent success — the bucket toast already fired. Region failure is
      // recoverable (operator can pick a region manually in production
      // settings) so we surface only a warning toast.
      onError: (err) => {
        toast({
          title: 'Bucket assigned but region not set',
          description: `Pick the region manually in Production Settings → Locations. (${err.message})`,
        });
      },
    });

  const updateTencentBucketMutation = trpc.locations.updateClientTencentBucket.useMutation({
    onSuccess: (_data, vars) => {
      toast({ title: 'Bucket assigned', description: 'Future prints for this client will use the new bucket.' });
      setOpenBucketPickerForClientId(null);
      // Clear the per-client pending state so reopening the picker reseeds
      // from the server-authoritative `assignedBucket` (avoids the
      // "previously-saved-value-sticks" leak flagged in post-impl review).
      setPendingBucketByClient((prev) => {
        if (!(vars.clientOrganizationId in prev)) return prev;
        const next = { ...prev };
        delete next[vars.clientOrganizationId];
        return next;
      });
      refreshAll();
    },
    onError: (err) => {
      toast({ title: 'Could not assign bucket', description: err.message, variant: 'destructive' });
    },
  });

  // Drop a client's in-progress print-folder edits so reopening the editor
  // reseeds from server-authoritative values (avoids the stale-pending leak).
  const clearPrintFoldersPending = (clientId: string) => {
    const drop = (prev: Record<string, string | null>) => {
      if (!(clientId in prev)) return prev;
      const next = { ...prev };
      delete next[clientId];
      return next;
    };
    setPendingCustomsFolderByClient(drop);
    setPendingPrintsFolderByClient(drop);
  };

  // BLU-641: per-junction print-folder (customs / prints) overrides. Mirrors
  // `updateClientTencentBucket` — owner-only, explicit-confirm, no keystroke
  // mutation. Cache-patches immediately so the summary line reflects the saved
  // value before refreshAll()'s background refetch reconciles with the server.
  const updatePrintFoldersMutation = trpc.locations.updateClientPrintFolders.useMutation({
    onSuccess: (_data, vars) => {
      utils.locations.listConnectedClients.setData(undefined, (prev) =>
        prev?.map((row) => {
          const junction = row as ConnectedRow;
          return junction.location_id === vars.locationId &&
            junction.client_organization_id === vars.clientOrganizationId
            ? ({ ...junction, customs_folder: vars.customsFolder, prints_folder: vars.printsFolder } as typeof row)
            : row;
        }),
      );
      toast({
        title: 'Print folders updated',
        description: 'Future prints for this client will use the new folders.',
      });
      setOpenPrintFoldersEditorForClientId(null);
      clearPrintFoldersPending(vars.clientOrganizationId);
      refreshAll();
    },
    onError: (err) => {
      toast({ title: 'Could not update print folders', description: err.message, variant: 'destructive' });
    },
  });

  const revokeMutation = trpc.locations.revokeClientAccess.useMutation({
    onSuccess: () => {
      toast({ title: 'Access removed' });
      refreshAll();
      setPendingRevoke(null);
    },
    onError: (err) => {
      toast({ title: 'Could not revoke', description: err.message, variant: 'destructive' });
    },
  });

  // Filter to junctions whose location matches THIS card's location.
  const connectedRows: ConnectedRow[] = useMemo(
    () =>
      ((connectedQuery.data ?? []) as ConnectedRow[]).filter(
        (r) => r.location_id === locationId,
      ),
    [connectedQuery.data, locationId],
  );
  const pendingRows: PendingRow[] = useMemo(
    () =>
      ((pendingQuery.data ?? []) as PendingRow[]).filter((r) => r.location_id === locationId),
    [pendingQuery.data, locationId],
  );

  const activeCount = connectedRows.filter((r) => r.status === 'active').length;
  const pendingCount = pendingRows.length;

  const codeIsSet = codeStatusQuery.data?.hasClaimCode === true;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              {locationName}: Clients
              {(activeCount > 0 || pendingCount > 0) && (
                <Badge variant="outline" className="text-xs font-normal">
                  {activeCount > 0 && <span>{activeCount} active</span>}
                  {activeCount > 0 && pendingCount > 0 && <span> · </span>}
                  {pendingCount > 0 && <span className="text-amber-700">{pendingCount} pending</span>}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Shopify-store clients you fulfil orders for from this warehouse.
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Claim code panel */}
        <div
          key={glowKey}
          ref={codePanelRef}
          className={`rounded-lg border bg-muted/30 p-4 ${
            glowKey > 0 ? 'motion-safe:animate-unlock-glow' : ''
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                Claim code
              </div>
              {latestClaimCode ? (
                <div className="font-mono text-2xl tracking-[0.2em] text-foreground select-all break-all">
                  {latestClaimCode}
                </div>
              ) : codeIsSet ? (
                <div className="text-sm text-muted-foreground">
                  A claim code is set, but we only show it once at generation. Rotate to view a new one.
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No active code. Generate one to share with a Shopify-store client.
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Share this with a Shopify-store client and they&rsquo;ll be able to route their orders to this warehouse. Existing connections won&rsquo;t be affected when you rotate the code; pending claims will need to retry with the new one.
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (!latestClaimCode) return;
                        navigator.clipboard.writeText(latestClaimCode).then(() => {
                          setCodeCopied(true);
                          window.setTimeout(() => setCodeCopied(false), 1400);
                        });
                      }}
                      disabled={!latestClaimCode}
                      aria-label="Copy claim code"
                    >
                      {codeCopied ? (
                        <Check className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{codeCopied ? 'Copied' : 'Copy code'}</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <AlertDialog open={rotateOpen} onOpenChange={setRotateOpen}>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label="Rotate claim code">
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                    </TooltipTrigger>
                    <TooltipContent>{codeIsSet ? 'Rotate code' : 'Generate code'}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {codeIsSet ? 'Rotate the claim code?' : 'Generate a claim code?'}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {codeIsSet
                        ? 'The current code will stop working immediately. Active client connections won’t be affected, but any in-flight claim attempts using the old code will need a new one. Pending claims you haven’t approved yet will be auto-cancelled.'
                        : 'A new code will be generated and shown to you once. Share it with a client to grant them fulfilment access.'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      disabled={rotateMutation.isPending}
                      onClick={() =>
                        rotateMutation.mutate({
                          locationId,
                          prefix: locationName.replace(/\s+/g, '').slice(0, 6) || 'LOC',
                        })
                      }
                    >
                      {rotateMutation.isPending
                        ? codeIsSet
                          ? 'Rotating…'
                          : 'Generating…'
                        : codeIsSet
                          ? 'Rotate code'
                          : 'Generate code'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>

        {/* Connected clients list */}
        {pendingRows.length === 0 && connectedRows.length === 0 ? (
          <div className="rounded-lg border border-dashed bg-card p-6 text-center">
            <MapPin className="mx-auto mb-2 h-5 w-5 text-muted-foreground" aria-hidden="true" />
            {/* DESIGN.md §14.3 specifies the first sentence in bold. */}
            <p className="text-sm font-semibold">No clients connected yet.</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
              Share the claim code with a Shopify-store client and they&rsquo;ll appear here once they accept.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {pendingRows.map((r) => {
              const pickerOpen = openApprovePickerForClientId === r.client_organization_id;
              // BLU-641 approve-panel bucket override (Tencent locations only).
              // `approveBucket` is card-level (one panel open at a time). Empty
              // or malformed → don't drive/submit a bucket (keeps today's flow).
              const approveBucketRaw = approveBucket.trim();
              const approveBucketNonEmptyInvalid =
                approveIsTencent
                && approveBucketRaw.length > 0
                && !TENCENT_BUCKET_NAME_RE.test(approveBucketRaw);
              const approveBucketValid =
                approveBucketRaw.length > 0 && TENCENT_BUCKET_NAME_RE.test(approveBucketRaw);
              const approveTencentBucketName =
                approveIsTencent && approveBucketValid ? approveBucketRaw : undefined;
              return (
                <div
                  key={`pending-${r.client_organization_id}`}
                  className="rounded-lg border bg-card p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{r.client_org?.name ?? 'Unknown organisation'}</span>
                        <Badge className="bg-amber-100 text-amber-800 ring-1 ring-amber-300/80 text-[10px] uppercase tracking-wide">
                          Pending review
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Claimed {formatDate(r.claimed_at)} ·{' '}
                        {r.client_shopify_location_id === null ? (
                          <span>Shopify location will be selected during app pairing</span>
                        ) : (
                          <>
                            binds to Shopify location{' '}
                            <span className="font-mono">{r.client_shopify_location_id}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      {/* BLU-368 Plan 47.02-05: two-step inline approval.
                          Toggle expands the panel — picker is inline, NOT modal
                          (modal-from-card banned by DESIGN.md §10). */}
                      <Button
                        size="sm"
                        onClick={() => {
                          setOpenApprovePickerForClientId(pickerOpen ? null : r.client_organization_id);
                          // Reset the override on open + close so a panel never
                          // inherits another row's typed bucket.
                          setApproveBucket('');
                        }}
                        disabled={approveMutation.isPending}
                        className="gap-1.5"
                      >
                        Approve access
                        {pickerOpen ? (
                          <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setPendingRevoke({
                            clientOrganizationId: r.client_organization_id,
                            clientName: r.client_org?.name ?? 'this client',
                            reason: 'denied',
                          })
                        }
                        disabled={revokeMutation.isPending}
                      >
                        Deny
                      </Button>
                    </div>
                  </div>
                  {pickerOpen && (
                    <div className="mt-3 space-y-3">
                      {/* BLU-641: on Tencent locations, let the operator pick
                          which bucket holds this client's print files BEFORE
                          browsing folders. Selecting one re-drives the picker
                          below (via bucketOverride) to that bucket's root.
                          Unset = today's behavior exactly. */}
                      {approveIsTencent && (
                        <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                          <div>
                            <p className="text-sm font-medium leading-snug">Print files bucket</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Optional. Pick the Tencent COS bucket that holds this client&rsquo;s print files, then browse to the folder below. Leave unset to use this location&rsquo;s default bucket.
                            </p>
                          </div>
                          <TencentBucketSelect
                            isLoading={tencentBucketsQuery.isPending}
                            data={tencentBucketsQuery.data}
                            value={approveBucket}
                            invalid={approveBucketNonEmptyInvalid}
                            onChange={setApproveBucket}
                            disabled={approveMutation.isPending}
                          />
                        </div>
                      )}
                      <FolderPicker
                        locationId={locationId}
                        clientOrganizationId={r.client_organization_id}
                        initialPrefix=""
                        bucketOverride={approveTencentBucketName}
                        confirmDisabled={approveBucketNonEmptyInvalid}
                        isMutating={approveMutation.isPending}
                        onCancel={() => {
                          setOpenApprovePickerForClientId(null);
                          setApproveBucket('');
                        }}
                        onConfirm={(prefix) =>
                          approveMutation.mutate({
                            locationId,
                            clientOrganizationId: r.client_organization_id,
                            ...(prefix ? { printFilePrefix: prefix } : {}),
                            ...(approveTencentBucketName
                              ? { tencentBucketName: approveTencentBucketName }
                              : {}),
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })}
            {connectedRows
              .filter((r) => r.status !== 'pending_approval')
              .map((r) => {
                const isActive = r.status === 'active';
                // Phase 47.02.2 Plan 04 + Plan 05 review fix: Edit folder shows on
                // any backend that uses per-client folder prefixes — Tencent COS
                // (BLU-368), GCS (Plan 02 factory derives `org_<owner>/clients/<client>/`),
                // and locations with NO explicit print_file_source (Plan 03 flipped
                // the runtime default to GCS, so unset === implicit GCS for routing
                // purposes; suppressing Edit folder there would leave new locations
                // in a button-hidden state until first manual toggle). Azure is the
                // only backend without a folder concept; we suppress the button only
                // for that explicit case.
                const source = r.locations?.production_settings?.print_file_source;
                const isFolderBased = source !== 'azure';
                const isTencent = source === 'tencent_cos';
                const editPickerOpen = openEditPickerForClientId === r.client_organization_id;
                const bucketPickerOpen = openBucketPickerForClientId === r.client_organization_id;
                const aliasEditorOpen = openAliasEditorForClientId === r.client_organization_id;
                const printFoldersEditorOpen =
                  openPrintFoldersEditorForClientId === r.client_organization_id;
                const prefix = r.print_file_prefix ?? '';
                const assignedBucket = r.tencent_bucket_name ?? '';
                const orgName = r.client_org?.name ?? 'Unknown organisation';
                const alias = r.client_alias ?? null;
                // BLU-641 print-folder overrides. null = inherit owner default,
                // '' = bucket root, string = explicit folder.
                const customsFolder = r.customs_folder ?? null;
                const printsFolder = r.prints_folder ?? null;
                // Shared renderer for the two identical folder fields (customs +
                // prints). The Checkbox toggles inherit (null) vs. override; an
                // empty override input means the bucket root ('').
                const renderFolderField = (
                  fieldLabel: string,
                  idPrefix: string,
                  value: string | null,
                  onValue: (next: string | null) => void,
                ) => {
                  const inherit = value === null;
                  const text = value ?? '';
                  const isRoot = !inherit && text.trim().length === 0;
                  const invalid = !inherit && text.length > 0 && isInvalidPrintFolder(text);
                  const inputId = `${idPrefix}-${r.client_organization_id}`;
                  return (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <label htmlFor={inputId} className="text-xs font-medium">
                          {fieldLabel}
                        </label>
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                          <Checkbox
                            checked={inherit}
                            onCheckedChange={(c) => onValue(c === true ? null : '')}
                            disabled={updatePrintFoldersMutation.isPending}
                          />
                          Inherit owner default
                        </label>
                      </div>
                      <input
                        id={inputId}
                        type="text"
                        value={text}
                        placeholder={inherit ? 'Owner default (inherited)' : 'bucket root'}
                        onChange={(e) => onValue(e.target.value)}
                        disabled={inherit || updatePrintFoldersMutation.isPending}
                        className={`h-9 w-full rounded-md border bg-background px-3 font-mono text-xs disabled:opacity-60 ${
                          invalid ? 'border-destructive' : ''
                        }`}
                        aria-invalid={invalid}
                        autoComplete="off"
                        spellCheck={false}
                      />
                      {invalid ? (
                        <p className="text-[10px] text-destructive">
                          Folders may use letters, digits, /, _, - only. No leading or trailing slash.
                        </p>
                      ) : isRoot ? (
                        // Empty is a deliberate choice (bucket root), so spell it
                        // out at the point of action to avoid setting root by accident.
                        <p className="text-[10px] text-muted-foreground/90">
                          Empty: files go to the bucket root.
                        </p>
                      ) : null}
                    </div>
                  );
                };
                return (
                  <div
                    key={`connected-${r.client_organization_id}`}
                    className="rounded-lg border bg-card p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{alias ?? orgName}</span>
                          {alias && (
                            <span className="text-xs text-muted-foreground font-normal">
                              aka {orgName}
                            </span>
                          )}
                          {isActive ? (
                            <Badge className="bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300/80 text-[10px] uppercase tracking-wide">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="ring-1 ring-border text-[10px] uppercase tracking-wide">
                              Revoked
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {isActive
                            ? `Claimed ${formatDate(r.claimed_at)} · approved ${formatDate(r.approved_at)} · ${
                              r.client_shopify_location_id === null
                                ? 'awaiting Shopify location selection in the app'
                                : `binds to Shopify location ${r.client_shopify_location_id}`
                            }`
                            : `Revoked${r.revoked_reason ? ` (${r.revoked_reason})` : ''}`}
                        </div>
                        {/* Third line: chosen folder. Only shown when set. */}
                        {isActive && prefix && (
                          <div className="text-xs font-mono text-muted-foreground mt-0.5">
                            · {prefix}
                          </div>
                        )}
                        {/* Phase 47.02.3-LITE — fourth line: assigned Tencent bucket.
                            Only shown on Tencent-routed locations to keep the row
                            quiet for GCS/Azure junctions. NULL = falls back to the
                            org Vault default; we surface that explicitly so an
                            operator can tell "assigned this bucket" apart from
                            "using org default". Icon lives on the action button —
                            we keep the line text-only here to avoid the duplicate-
                            icon visual collision flagged in the post-impl review. */}
                        {isActive && isTencent && (
                          <div className="text-xs font-mono text-muted-foreground mt-0.5 truncate">
                            ·{' '}
                            {assignedBucket || <span className="italic">org default bucket</span>}
                          </div>
                        )}
                        {/* BLU-641 fifth line: per-client print-folder overrides.
                            Only shown when at least one is set, so default rows
                            (both inherited) stay quiet; the button + editor carry
                            the affordance otherwise. */}
                        {isActive
                          && isFolderBased
                          && (customsFolder !== null || printsFolder !== null) && (
                          <div className="text-xs text-muted-foreground mt-0.5 truncate">
                            Print folders: customs{' '}
                            {customsFolder === null ? (
                              <span className="italic">inherited</span>
                            ) : customsFolder === '' ? (
                              <span className="italic">bucket root</span>
                            ) : (
                              <span className="font-mono">{customsFolder}</span>
                            )}
                            {' · '}prints{' '}
                            {printsFolder === null ? (
                              <span className="italic">inherited</span>
                            ) : printsFolder === '' ? (
                              <span className="italic">bucket root</span>
                            ) : (
                              <span className="font-mono">{printsFolder}</span>
                            )}
                          </div>
                        )}
                        {/* BLU-711 sixth line: live shared-SKU mappings between
                            this owner and this client. Count + link only — the
                            mapping surface itself is /products; a row with no
                            mappings stays silent like the lines above. */}
                        {isActive && (
                          <SharedSkuMappingLine clientOrganizationId={r.client_organization_id} />
                        )}
                      </div>
                      {isActive && (
                        <div className="flex gap-1.5">
                          {/* Edit nickname — source-agnostic, unlike the folder/
                              bucket affordances below which are gated to
                              specific print_file_source backends. */}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-3 text-xs gap-1.5"
                            onClick={() => {
                              setOpenAliasEditorForClientId(aliasEditorOpen ? null : r.client_organization_id);
                              setPendingAliasByClient((prev) => ({
                                ...prev,
                                [r.client_organization_id]: prev[r.client_organization_id] ?? (alias ?? ''),
                              }));
                            }}
                            disabled={updateAliasMutation.isPending}
                          >
                            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                            Edit nickname
                          </Button>
                          {/* Edit folder — constructive left, destructive right.
                              Always present on folder-based backends (Tencent COS +
                              GCS); visible even without prefix so operator can ADD
                              a folder. Hidden on Azure-routed (no folder concept). */}
                          {isFolderBased && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-3 text-xs gap-1.5"
                              onClick={() =>
                                setOpenEditPickerForClientId(editPickerOpen ? null : r.client_organization_id)
                              }
                              disabled={updatePrefixMutation.isPending}
                            >
                              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                              Edit folder
                            </Button>
                          )}
                          {/* Phase 47.02.3-LITE: per-junction bucket assignment.
                              Tencent-only — GCS routes via UUID prefix inside the
                              shared bucket, and Azure has no bucket concept at
                              this layer. */}
                          {isTencent && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-3 text-xs gap-1.5"
                              onClick={() => {
                                setOpenBucketPickerForClientId(
                                  bucketPickerOpen ? null : r.client_organization_id,
                                );
                                // Seed the pending value with whatever's already
                                // assigned so the dropdown starts on the current
                                // selection rather than blank.
                                setPendingBucketByClient((prev) => ({
                                  ...prev,
                                  [r.client_organization_id]: prev[r.client_organization_id] ?? assignedBucket,
                                }));
                              }}
                              disabled={updateTencentBucketMutation.isPending}
                            >
                              <Server className="h-3.5 w-3.5" aria-hidden="true" />
                              Assign bucket
                            </Button>
                          )}
                          {/* BLU-641: per-client customs/prints folder overrides.
                              Same folder-based gate as Edit folder (Azure routes
                              by container, not folder). */}
                          {isFolderBased && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-3 text-xs gap-1.5"
                              onClick={() => {
                                if (printFoldersEditorOpen) {
                                  setOpenPrintFoldersEditorForClientId(null);
                                  clearPrintFoldersPending(r.client_organization_id);
                                } else {
                                  setOpenPrintFoldersEditorForClientId(r.client_organization_id);
                                  // Seed both fields from the current server values.
                                  setPendingCustomsFolderByClient((prev) => ({
                                    ...prev,
                                    [r.client_organization_id]: customsFolder,
                                  }));
                                  setPendingPrintsFolderByClient((prev) => ({
                                    ...prev,
                                    [r.client_organization_id]: printsFolder,
                                  }));
                                }
                              }}
                              disabled={updatePrintFoldersMutation.isPending}
                            >
                              <FolderTree className="h-3.5 w-3.5" aria-hidden="true" />
                              Print folders
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-orange-300 text-orange-700 hover:bg-orange-50"
                            onClick={() =>
                              setPendingRevoke({
                                clientOrganizationId: r.client_organization_id,
                                clientName: r.client_org?.name ?? 'this client',
                                reason: 'revoked_by_owner',
                              })
                            }
                            disabled={revokeMutation.isPending}
                          >
                            Remove access
                          </Button>
                        </div>
                      )}
                    </div>
                    {isActive && (
                      <ShopifyConnectionPanel
                        locationId={locationId}
                        clientOrganizationId={r.client_organization_id}
                        clientName={alias ?? orgName}
                      />
                    )}
                    {/* BLU per-client shipping + BLU C2 write-back: the
                        provider box, then the write-back switch it decides
                        (write-back only acts on Manual). Always visible, not
                        an expandable editor, so the state reads at a glance.
                        Translated; see ClientShippingControls. */}
                    {isActive && (
                      <ClientShippingControls
                        clientOrganizationId={r.client_organization_id}
                        shippingProvider={r.shipping_provider}
                        locationDefault={locationDefault}
                        writebackEnabled={r.fulfillment_writeback_enabled === true}
                        handoverTag={r.handover_shopify_tag}
                        providerRefusal={
                          shippingRefusal?.locationId === locationId &&
                          shippingRefusal.clientOrganizationId === r.client_organization_id
                            ? shippingRefusal.code
                            : null
                        }
                        providerSaving={updateShippingProviderMutation.isPending}
                        writebackSaving={updateWritebackMutation.isPending}
                        onProviderChange={(shippingProvider, handoverTag) =>
                          updateShippingProviderMutation.mutate({
                            locationId,
                            clientOrganizationId: r.client_organization_id,
                            shippingProvider,
                            ...(shippingProvider === 'handover' ? { handoverTag } : {}),
                          })
                        }
                        onRequestWritebackOn={() =>
                          setPendingWritebackConfirm({
                            clientOrganizationId: r.client_organization_id,
                            clientName: alias ?? orgName,
                          })
                        }
                        onWritebackOff={() =>
                          updateWritebackMutation.mutate({
                            locationId,
                            clientOrganizationId: r.client_organization_id,
                            enabled: false,
                          })
                        }
                      />
                    )}
                    {/* BLU-711 A1: let this client adjust the stock they own
                        here. Always visible alongside write-back so both
                        authority grants read at a glance. */}
                    {isActive && (
                      <JunctionGrantRow
                        id={`client-stock-${r.client_organization_id}`}
                        helpId={`client-stock-help-${r.client_organization_id}`}
                        label="Let this client adjust their own stock here"
                        help={
                          <>
                            Their admins and warehouse users can set stock levels, receive stock, run stocktakes (including bulk CSV), record scrap, move stock to Unavailable and edit restock thresholds, but only for the SKUs they own at this location. They cannot change your stock, another client&rsquo;s stock, or print technology at this location.
                          </>
                        }
                        control={
                          <Switch
                            id={`client-stock-${r.client_organization_id}`}
                            aria-describedby={`client-stock-help-${r.client_organization_id}`}
                            checked={r.client_stock_writes_enabled === true}
                            // No `disabled` while pending: a disabled focused
                            // Radix Switch blurs to <body>, dropping a keyboard
                            // user's place. The optimistic patch already moves
                            // the thumb; just swallow re-clicks on THIS row
                            // until the mutation settles.
                            onCheckedChange={(checked) => {
                              if (
                                updateClientStockMutation.isPending &&
                                updateClientStockMutation.variables?.clientOrganizationId ===
                                  r.client_organization_id
                              ) {
                                return;
                              }
                              updateClientStockMutation.mutate({
                                locationId,
                                clientOrganizationId: r.client_organization_id,
                                enabled: checked,
                              });
                            }}
                          />
                        }
                      />
                    )}
                    {/* Phase 92 (D-08, D-22): may this client build products on
                        our shared blanks? Owner-only, directly under the
                        stock-writes grant; turning it off asks first, because
                        the client's products on our blanks go out of stock.
                        Translated; see ClientBlankSupplyGrant. */}
                    {isActive && (
                      <ClientBlankSupplyGrant
                        locationId={locationId}
                        locationName={locationName}
                        clientOrganizationId={r.client_organization_id}
                        clientName={alias ?? orgName}
                        enabled={r.client_blank_supply_enabled === true}
                      />
                    )}
                    {/* BLU-711 A2: the other direction — what happens to a
                        count WE run on their rows. Sits next to the stock-writes
                        toggle so both stock authorities read at a glance. */}
                    {isActive && (
                      <JunctionGrantRow
                        id={`stocktake-review-${r.client_organization_id}`}
                        helpId={`stocktake-review-help-${r.client_organization_id}`}
                        label="Stocktake review"
                        help={
                          <>
                            When you count this client&rsquo;s stock here, choose whether the new
                            figures apply straight away or go to them for approval first. On
                            approval the count applies exactly as it does today, as a normal
                            stocktake batch you can undo for 30 days. You can change this at any
                            time.
                          </>
                        }
                        control={
                          <Select
                            value={r.stocktake_review_mode ?? 'auto_apply'}
                            onValueChange={(value) =>
                              updateStocktakeReviewModeMutation.mutate({
                                locationId,
                                clientOrganizationId: r.client_organization_id,
                                mode: value as 'auto_apply' | 'review',
                              })
                            }
                            // Same discipline as the shipping-provider select
                            // above: a second pick mid-flight races the first.
                            disabled={updateStocktakeReviewModeMutation.isPending}
                          >
                            <SelectTrigger
                              id={`stocktake-review-${r.client_organization_id}`}
                              aria-describedby={`stocktake-review-help-${r.client_organization_id}`}
                              className="h-8 w-44 shrink-0 text-xs"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="auto_apply">Apply immediately</SelectItem>
                              <SelectItem value="review">Send for approval</SelectItem>
                            </SelectContent>
                          </Select>
                        }
                      />
                    )}
                    {aliasEditorOpen && (
                      <div className="mt-3 rounded-md border bg-muted/30 p-3 space-y-2">
                        <label
                          className="text-xs text-muted-foreground"
                          htmlFor={`alias-${r.client_organization_id}`}
                        >
                          Nickname for {orgName}
                        </label>
                        <input
                          id={`alias-${r.client_organization_id}`}
                          type="text"
                          placeholder={orgName}
                          value={pendingAliasByClient[r.client_organization_id] ?? ''}
                          onChange={(e) =>
                            setPendingAliasByClient((prev) => ({
                              ...prev,
                              [r.client_organization_id]: e.target.value,
                            }))
                          }
                          maxLength={40}
                          className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                          autoComplete="off"
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-3 text-xs"
                            onClick={() => {
                              setOpenAliasEditorForClientId(null);
                              setPendingAliasByClient((prev) => {
                                if (!(r.client_organization_id in prev)) return prev;
                                const next = { ...prev };
                                delete next[r.client_organization_id];
                                return next;
                              });
                            }}
                            disabled={updateAliasMutation.isPending}
                          >
                            Cancel
                          </Button>
                          {alias && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-3 text-xs"
                              onClick={() =>
                                updateAliasMutation.mutate({
                                  locationId,
                                  clientOrganizationId: r.client_organization_id,
                                  clientAlias: null,
                                })
                              }
                              disabled={updateAliasMutation.isPending}
                            >
                              Clear nickname
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="h-8 px-3 text-xs"
                            onClick={() => {
                              const pending = (pendingAliasByClient[r.client_organization_id] ?? '').trim();
                              const next = pending.length > 0 ? pending : null;
                              if (next === alias) {
                                setOpenAliasEditorForClientId(null);
                                return;
                              }
                              updateAliasMutation.mutate({
                                locationId,
                                clientOrganizationId: r.client_organization_id,
                                clientAlias: next,
                              });
                            }}
                            disabled={updateAliasMutation.isPending}
                          >
                            {updateAliasMutation.isPending && (
                              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" aria-hidden="true" />
                            )}
                            Save nickname
                          </Button>
                        </div>
                      </div>
                    )}
                    {editPickerOpen && (
                      <div className="mt-3">
                        <FolderPicker
                          locationId={locationId}
                          clientOrganizationId={r.client_organization_id}
                          initialPrefix={prefix}
                          isMutating={updatePrefixMutation.isPending}
                          onCancel={() => setOpenEditPickerForClientId(null)}
                          onConfirm={(newPrefix) =>
                            updatePrefixMutation.mutate({
                              locationId,
                              clientOrganizationId: r.client_organization_id,
                              printFilePrefix: newPrefix || null,
                            })
                          }
                        />
                      </div>
                    )}
                    {bucketPickerOpen && (
                      <div className="mt-3 rounded-md border bg-muted/30 p-3 space-y-3">
                        {(() => {
                          const pendingValue =
                            pendingBucketByClient[r.client_organization_id] ?? assignedBucket;
                          const freeTextInvalid =
                            tencentBucketsQuery.data?.supported === false
                            && pendingValue.length > 0
                            && !TENCENT_BUCKET_NAME_RE.test(pendingValue);
                          return (
                            <TencentBucketSelect
                              isLoading={tencentBucketsQuery.isPending}
                              data={tencentBucketsQuery.data}
                              value={pendingValue}
                              invalid={freeTextInvalid}
                              onChange={(v) =>
                                setPendingBucketByClient((prev) => ({
                                  ...prev,
                                  [r.client_organization_id]: v,
                                }))
                              }
                              disabled={updateTencentBucketMutation.isPending}
                            />
                          );
                        })()}
                        {/* Dropdown-only helper — the free-text fallback carries
                            its own format hint inside TencentBucketSelect. */}
                        {!tencentBucketsQuery.isPending
                          && tencentBucketsQuery.data?.supported !== false && (
                          <p className="text-[10px] text-muted-foreground/90">
                            Pick the bucket where this client&rsquo;s print files live.
                            Leave blank to fall back to the org default.
                          </p>
                        )}
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-3 text-xs"
                            onClick={() => {
                              setOpenBucketPickerForClientId(null);
                              // Reset the per-client pending entry so the
                              // next open reseeds from the authoritative
                              // assignedBucket — without this the in-flight
                              // unsaved value sticks across open/close.
                              setPendingBucketByClient((prev) => {
                                if (!(r.client_organization_id in prev)) return prev;
                                const next = { ...prev };
                                delete next[r.client_organization_id];
                                return next;
                              });
                            }}
                            disabled={updateTencentBucketMutation.isPending}
                          >
                            Cancel
                          </Button>
                          {/* Clear-to-default — distinct from Save with empty value
                              because operators reading the row want to see "fell back"
                              as an explicit action. */}
                          {assignedBucket && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 px-3 text-xs"
                              onClick={() =>
                                updateTencentBucketMutation.mutate({
                                  locationId,
                                  clientOrganizationId: r.client_organization_id,
                                  tencentBucketName: null,
                                })
                              }
                              disabled={updateTencentBucketMutation.isPending}
                            >
                              Clear assignment
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="h-8 px-3 text-xs"
                            onClick={() => {
                              const pending = (pendingBucketByClient[r.client_organization_id] ?? '').trim();
                              if (!pending || pending === assignedBucket) {
                                setOpenBucketPickerForClientId(null);
                                return;
                              }
                              // Belt-and-suspenders: refuse to fire the mutation
                              // when the value is malformed (button disable
                              // below already prevents this; keeps the click
                              // handler defensive if a future refactor regresses
                              // the disabled state).
                              if (!TENCENT_BUCKET_NAME_RE.test(pending)) {
                                return;
                              }
                              // Phase 47.02.3-LITE region/bucket coupling — if
                              // the picked bucket came from the dropdown
                              // (vs the free-text fallback), the entry carries
                              // a region. Co-write that to the location's
                              // production_settings to keep the SDK endpoint
                              // aligned with where the bucket actually lives.
                              // Free-text path has no region; operator must
                              // set per-location region manually.
                              const bucketEntry = tencentBucketsQuery.data?.supported === true
                                ? tencentBucketsQuery.data.buckets.find((b) => b.name === pending)
                                : undefined;
                              const bucketRegion = bucketEntry?.region ?? null;
                              const currentRegion = r.locations?.production_settings?.tencent_region ?? null;
                              const TENCENT_REGION_VALUES = ['ap-guangzhou', 'ap-shanghai', 'ap-shenzhen-fsi', 'ap-beijing'] as const;
                              type LiteTencentRegion = (typeof TENCENT_REGION_VALUES)[number];
                              const isValidRegion = (v: string | null): v is LiteTencentRegion =>
                                v !== null && (TENCENT_REGION_VALUES as readonly string[]).includes(v);

                              updateTencentBucketMutation.mutate(
                                {
                                  locationId,
                                  clientOrganizationId: r.client_organization_id,
                                  tencentBucketName: pending,
                                },
                                {
                                  onSuccess: () => {
                                    // Best-effort region co-write. Skip when:
                                    // (a) no region known (free-text path), or
                                    // (b) region already matches location setting.
                                    if (
                                      isValidRegion(bucketRegion)
                                      && bucketRegion !== currentRegion
                                    ) {
                                      setLocationTencentRegionFromBucketMutation.mutate({
                                        locationId,
                                        tencentRegion: bucketRegion,
                                      });
                                    }
                                  },
                                },
                              );
                            }}
                            disabled={
                              updateTencentBucketMutation.isPending
                              || (() => {
                                const pending = (pendingBucketByClient[r.client_organization_id] ?? '').trim();
                                // Allow Save when empty (Cancel-equivalent) or
                                // unchanged (no-op); block when malformed.
                                if (!pending || pending === assignedBucket) return false;
                                return !TENCENT_BUCKET_NAME_RE.test(pending);
                              })()
                            }
                          >
                            {updateTencentBucketMutation.isPending && (
                              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" aria-hidden="true" />
                            )}
                            Save bucket
                          </Button>
                        </div>
                      </div>
                    )}
                    {printFoldersEditorOpen && (
                      <div className="mt-3 rounded-md border bg-muted/30 p-3 space-y-3">
                        <p className="text-xs text-muted-foreground">
                          Where {alias ?? orgName}&rsquo;s print files live inside the bucket. Enter a folder for each, leave a field empty for the bucket root, or tick &lsquo;Inherit owner default&rsquo; to fall back to this location&rsquo;s owner-level folder.
                        </p>
                        {renderFolderField(
                          'Customs artwork folder',
                          'customs-folder',
                          pendingCustomsFolderByClient[r.client_organization_id] ?? null,
                          (next) =>
                            setPendingCustomsFolderByClient((prev) => ({
                              ...prev,
                              [r.client_organization_id]: next,
                            })),
                        )}
                        {renderFolderField(
                          'Print files folder',
                          'prints-folder',
                          pendingPrintsFolderByClient[r.client_organization_id] ?? null,
                          (next) =>
                            setPendingPrintsFolderByClient((prev) => ({
                              ...prev,
                              [r.client_organization_id]: next,
                            })),
                        )}
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-3 text-xs"
                            onClick={() => {
                              setOpenPrintFoldersEditorForClientId(null);
                              clearPrintFoldersPending(r.client_organization_id);
                            }}
                            disabled={updatePrintFoldersMutation.isPending}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            className="h-8 px-3 text-xs"
                            onClick={() => {
                              const cVal =
                                pendingCustomsFolderByClient[r.client_organization_id] ?? null;
                              const pVal =
                                pendingPrintsFolderByClient[r.client_organization_id] ?? null;
                              // No-op when unchanged.
                              if (cVal === customsFolder && pVal === printsFolder) {
                                setOpenPrintFoldersEditorForClientId(null);
                                clearPrintFoldersPending(r.client_organization_id);
                                return;
                              }
                              // Defensive: never fire the mutation on an empty or
                              // malformed override (the Save button is already
                              // disabled below).
                              if (
                                isPrintFolderOverrideInvalid(cVal)
                                || isPrintFolderOverrideInvalid(pVal)
                              ) {
                                return;
                              }
                              updatePrintFoldersMutation.mutate({
                                locationId,
                                clientOrganizationId: r.client_organization_id,
                                customsFolder: cVal,
                                printsFolder: pVal,
                              });
                            }}
                            disabled={
                              updatePrintFoldersMutation.isPending
                              || (() => {
                                const cVal =
                                  pendingCustomsFolderByClient[r.client_organization_id] ?? null;
                                const pVal =
                                  pendingPrintsFolderByClient[r.client_organization_id] ?? null;
                                return (
                                  isPrintFolderOverrideInvalid(cVal)
                                  || isPrintFolderOverrideInvalid(pVal)
                                );
                              })()
                            }
                          >
                            {updatePrintFoldersMutation.isPending && (
                              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" aria-hidden="true" />
                            )}
                            Save folders
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}

        {/* BLU C2: enabling write-back starts writing to the client's live
            Shopify store on their next manual ship — confirm before it fires,
            same guard pattern as the revoke dialog below. */}
        <AlertDialog
          open={pendingWritebackConfirm !== null}
          onOpenChange={(next) => !next && setPendingWritebackConfirm(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Enable fulfilment write-back for {pendingWritebackConfirm?.clientName ?? 'this client'}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Manual completion for {pendingWritebackConfirm?.clientName ?? 'this client'} at this location will mark their orders fulfilled directly on their live Shopify store. You can turn this off at any time.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={updateWritebackMutation.isPending}
                onClick={() => {
                  if (!pendingWritebackConfirm) return;
                  updateWritebackMutation.mutate({
                    locationId,
                    clientOrganizationId: pendingWritebackConfirm.clientOrganizationId,
                    enabled: true,
                  });
                }}
              >
                {updateWritebackMutation.isPending ? 'Enabling…' : 'Enable write-back'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Revoke confirmation — destructive action requires an explicit guard. */}
        <AlertDialog
          open={pendingRevoke !== null}
          onOpenChange={(next) => !next && setPendingRevoke(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {pendingRevoke?.reason === 'denied'
                  ? `Deny ${pendingRevoke?.clientName}'s claim?`
                  : `Remove ${pendingRevoke?.clientName}'s access?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {pendingRevoke?.reason === 'denied'
                  ? `${pendingRevoke?.clientName} won't be able to fulfil from this warehouse. They can submit a new claim if you share the code again.`
                  : `${pendingRevoke?.clientName}'s orders will stop routing to this warehouse. Orders already in progress will finish normally. You can re-grant access at any time by sharing a claim code.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-700 bg-transparent"
                disabled={revokeMutation.isPending}
                onClick={() => {
                  if (!pendingRevoke) return;
                  revokeMutation.mutate({
                    locationId,
                    clientOrganizationId: pendingRevoke.clientOrganizationId,
                    reason: pendingRevoke.reason,
                  });
                }}
              >
                {revokeMutation.isPending
                  ? 'Removing…'
                  : pendingRevoke?.reason === 'denied'
                    ? 'Deny claim'
                    : 'Remove access'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

/**
 * BLU-711 Part B: junction-card line for live shared-SKU mappings between this
 * owner org and one client. Count + link ONLY — nomination lives in
 * EditSkuModal and the /products bulk bar, and mapping management is Stage 2;
 * this line exists so an owner can see that a client is wired to their pool
 * without hunting the catalog.
 *
 * Renders nothing at zero (same "default rows stay quiet" rule as the folder
 * and print-folder lines above) and nothing while loading, so the card does
 * not shift as counts arrive. The count is owner-scoped server-side (mappings
 * whose owner_organization_id is the caller), so a non-owner simply sees
 * nothing rather than a leaked figure.
 *
 * The rest of `features/locations/**` is hardcoded English, but this line is
 * translated deliberately: the owner org reading it is the 3PL (Thread Lab),
 * whose operators run the app in zh. Scoped to this subcomponent so the
 * surrounding file keeps its existing convention.
 */
function SharedSkuMappingLine({ clientOrganizationId }: { clientOrganizationId: string }) {
  const { t } = useTranslation('inventory');
  const { data } = useClientSharedSkuMappingCount(clientOrganizationId);
  const count = data?.count ?? 0;
  if (count === 0) return null;
  return (
    <div className="text-xs text-muted-foreground mt-0.5">
      · {t('sharedSkus.junctionMappingCount', { count })}{' '}
      <Link to="/products" className="underline underline-offset-2 hover:text-foreground">
        {t('sharedSkus.junctionMappingLink')}
      </Link>
    </div>
  );
}
